import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMissions, updateMission } from '../services/missionService';
import { useOrganization } from '../contexts/OrganizationContext';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import CONFIG from '../config/config';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import { Box, Typography, Button, LinearProgress } from '@mui/material';
import MetricBox from '../components/MetricBox';
import AddIcon from '@mui/icons-material/Add';

mapboxgl.accessToken = CONFIG.MAPBOX.PUBLIC_TOKEN;

const Dashboard = () => {
  let primaryColor = '#2c3e50';
  const { selectedOrg, updateSelectedOrg } = useOrganization();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const navigate = useNavigate();

  const totalMissions = missions.length;
  const completedMissions = missions.filter(m => m.completedAt).length;
  const pendingMissions = missions.filter(m => !m.completedAt).length;

  // State variables to hold changes
  const [changeInTotalMissions, setChangeInTotalMissions] = useState(0);
  const [changeInCompletedMissions, setChangeInCompletedMissions] = useState(0);
  const [changeInPendingMissions, setChangeInPendingMissions] = useState(0);
  const [flightTimeChange, setFlightTimeChange] = useState(0);
  const [flightTimeTrendingUp, setFlightTimeTrendingUp] = useState(true);

  const totalFlightTime = missions.reduce((acc, m) => {
    if (m.startedAt && m.completedAt) {
      const startedAt = new Date(m.startedAt.$date || m.startedAt).getTime(); // Parse ISO8601 date
      const completedAt = new Date(m.completedAt.$date || m.completedAt).getTime(); // Parse ISO8601 date
      const flightTimeInMinutes = Math.max(0, (completedAt - startedAt) / 60000); // Convert ms to minutes
      return acc + flightTimeInMinutes;
    }
    return acc;
  }, 0);

  const totalFlightTimeInHours = (totalFlightTime / 60).toFixed(0); // to hours and minutes
  const formattedFlightTime = `${totalFlightTimeInHours} hrs`;

  const calculateFlightTimeForPeriod = (missions, startDate, endDate) => {
    return missions.reduce((acc, mission) => {
      const createdAt = new Date(mission.createdAt?.$date || mission.createdAt);
      if (mission.startedAt && mission.completedAt && createdAt >= startDate && createdAt <= endDate) {
        const startedAt = new Date(mission.startedAt.$date || mission.startedAt).getTime();
        const completedAt = new Date(mission.completedAt.$date || mission.completedAt).getTime();
        const flightTimeInMinutes = Math.max(0, (completedAt - startedAt) / 60000); // Convert ms to minutes
        return acc + flightTimeInMinutes;
      }
      return acc;
    }, 0);
  };

  const recentMissions = missions
    .sort((a, b) => new Date(b.completedAt?.$date || 0) - new Date(a.completedAt?.$date || 0))
    .slice(0, 5);

  useEffect(() => {
    const getMissions = async () => {
      if (!selectedOrg) {
        setError('No organization selected.');
        setLoading(false);
        return;
      }

      try {
        const data = await fetchMissions(selectedOrg);
        const validMissions = data.filter(mission => !mission.deletedAt);
        const sortedMissions = validMissions.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setMissions(sortedMissions);
        setFilteredMissions(sortedMissions);
      } catch (err) {
        setError('Failed to fetch missions');
      } finally {
        setLoading(false);
      }
    };

    getMissions();

    return () => {
    };
  }, [selectedOrg]);

  useEffect(() => {
    const calculateChanges = () => {
      const now = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(now.getDate() - 30);

      // Missions within the last 30 days
      const missionsInLast30Days = missions.filter(mission => {
        const createdAt = new Date(mission.createdAt?.$date || mission.createdAt);
        return createdAt >= thirtyDaysAgo && createdAt <= now;
      });

      // Total missions in the last 30 days
      const totalMissionsLast30Days = missionsInLast30Days.length;

      // Completed missions in the last 30 days
      const completedMissionsLast30Days = missionsInLast30Days.filter(mission => mission.completedAt).length;

      // Pending missions in the last 30 days
      const pendingMissionsLast30Days = missionsInLast30Days.filter(mission => !mission.completedAt).length;

      // Previous totals for comparison
      const totalMissionsBefore30Days = missions.length - totalMissionsLast30Days;
      const completedMissionsBefore30Days = missions.filter(mission => {
        const createdAt = new Date(mission.createdAt?.$date || mission.createdAt);
        return createdAt < thirtyDaysAgo && mission.completedAt;
      }).length;
      const pendingMissionsBefore30Days = missions.filter(mission => {
        const createdAt = new Date(mission.createdAt?.$date || mission.createdAt);
        return createdAt < thirtyDaysAgo && !mission.completedAt;
      }).length;

      // Calculate changes
      setChangeInTotalMissions(totalMissionsLast30Days - totalMissionsBefore30Days);
      setChangeInCompletedMissions(completedMissionsLast30Days - completedMissionsBefore30Days);
      setChangeInPendingMissions(pendingMissionsLast30Days - pendingMissionsBefore30Days);
    };

    if (missions.length > 0) {
      calculateChanges();
    }
  }, [missions]);

  useEffect(() => {
    const calculateFlightTimeChanges = () => {
      const now = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(now.getDate() - 30);

      // Flight time for missions in the last 30 days
      const flightTimeLast30Days = calculateFlightTimeForPeriod(missions, thirtyDaysAgo, now);

      // Flight time for missions before the last 30 days
      const flightTimeBefore30Days = calculateFlightTimeForPeriod(
        missions,
        new Date(0), // Start of time
        thirtyDaysAgo
      );

      // Calculate change and trend
      const change = ((flightTimeLast30Days - flightTimeBefore30Days) / 60).toFixed(1); // Convert minutes to hours
      const isUp = flightTimeLast30Days > flightTimeBefore30Days;

      setFlightTimeChange(change);
      setFlightTimeTrendingUp(isUp);
    };

    if (missions.length > 0) {
      calculateFlightTimeChanges();
    }
  }, [missions]);


  const handleAddMission = () => {
    navigate('/mission/new');
  };

  // Initialize the map
  /*
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      // Check if mapContainerRef is properly bound
      if (!mapContainerRef.current) {
        console.error('mapContainerRef is not defined');
        return;
      }

      // Initialize Mapbox map
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current, // Ensure this is defined
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [-98.5795, 39.8283], // Default center (USA)
        zoom: 4,
      });

      // Add navigation controls
      mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    }

    return () => {
      if (mapRef.current) mapRef.current.remove();
    };
  }, [mapContainerRef.current]);
*/

  // Add mission markers
  /*
  useEffect(() => {
    if (mapRef.current && missions?.length > 0) {
      missions.forEach((mission) => {
        if (mission.lat && mission.lon) {
          const marker = new mapboxgl.Marker()
            .setLngLat([mission.lon, mission.lat])
            .setPopup(
              new mapboxgl.Popup().setHTML(
                `<strong>${mission.missionName || 'Unnamed Mission'}</strong><br>
                  ${mission.jsonPlaceData?.preferredAddress || 'No Address Available'}`
              )
            )
            .addTo(mapRef.current);
        }
      });
    }
  }, [missions]);
*/

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#f9f9f9',
        }}
      >
        <Typography
          sx={{
            textAlign: 'center',
            color: '#2c3e50',
            fontWeight: 'bold',
          }}
        >
          Loading Personnel...
        </Typography>
        <LinearProgress
          sx={{
            height: 4,
            borderRadius: 4,
            backgroundColor: '#e0e0e0', // Track color
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: '#3f51b5', // Bar color
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 0, backgroundColor: '#f9f9f9' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 1,
          paddingLeft: '16px',
          paddingRight: '24px',
        }}
      >
        {/* Left-aligned content */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center', // Vertically center icon and text
            gap: 1, // Add spacing between the icon and text
          }}
        >
          <DashboardIcon sx={{ fontSize: '24px', color: '#2c3e50' }} />
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: '#2c3e50' }}
          >
            Dashboard
          </Typography>
        </Box>

        {/* Right-aligned button */}
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddMission}
        >
          Add Mission
        </Button>
      </Box>

      <div style={{ padding: '24px' }}>
        <h2 className="recent-missions-heading">Overview</h2>
        {/* Key Metrics */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '32px' }}>

          <MetricBox
            title="Total Missions"
            value={totalMissions}
            infoText="The total number of missions that have been created and are accessible in this organization."
            changeValue={changeInTotalMissions}
            isTrendingUp={changeInTotalMissions > 0}
          />
          <MetricBox
            title="Completed Missions"
            value={completedMissions}
            infoText="The total number of missions that have been successfully completed within this organization."
            changeValue={changeInCompletedMissions}
            isTrendingUp={changeInCompletedMissions > 0}
          />
          <MetricBox
            title="Pending Missions"
            value={pendingMissions}
            infoText="The number of missions that have been started but not yet completed or are still awaiting initiation."
            changeValue={changeInPendingMissions}
            isTrendingUp={changeInPendingMissions > 0}
          />
          <MetricBox
            title="Total Flight Time"
            value={formattedFlightTime}
            infoText="The cumulative time spent flying across all missions, calculated by summing the duration of each mission from its start to completion."
            changeValue={flightTimeChange}
            isTrendingUp={flightTimeTrendingUp}
          />
        </div>

        {/* Recent Missions */}
        <div style={{ marginBottom: '32px' }}>
          <h2 className="recent-missions-heading">Recent Missions</h2>
          <ul>
            {recentMissions.map(m => {
              // Calculate the actual flight time dynamically
              const startedAt = new Date(m.startedAt?.$date || m.startedAt).getTime();
              const completedAt = new Date(m.completedAt?.$date || m.completedAt).getTime();
              const actualFlightTime = Math.max(0, Math.round((completedAt - startedAt) / 60000)); // Flight time in minutes

              return (
                <Box sx={{ marginLeft: '16px', marginRight: '16px' }}>
                  <li key={m.missionGuid}>
                    <strong style={{ fontWeight: '500' }}>{m.missionName || 'Unnamed Mission'}</strong> -
                    Completed: {new Date(m.completedAt).toLocaleString()} -
                    Flight Time: {actualFlightTime} minutes -
                    Weather: {m.weatherData?.weather?.[0]?.description || 'No Data'}
                  </li>
                </Box>
              );
            })}
          </ul>
        </div>


        {/* Advisories 
      <div>
        <h2>Mission Advisories</h2>
        {missions.map(m => (
          <div key={m.missionGuid} style={{ padding: '8px', border: '1px solid #ccc', marginBottom: '8px' }}>
            <strong>{m.missionName || 'Unnamed Mission'}</strong><br />
            Advisory: {m.advisoryClearToFly
              ? 'Clear to Fly'
              : m.advisoryFlyWithApproval
                ? 'Approval Needed'
                : 'Restricted'}
          </div>
        ))}
      </div>
      */}

        {/* Recent Activity 
        <div style={{ marginBottom: '32px' }}>
          <h2 className="recent-missions-heading">Recent Activity</h2>
          <ul>
            <li>Mission "Mission Alpha" completed on Jan 10, 2025</li>
            <li>Organization "SkyTrack" onboarded</li>
            <li>New flight log added: 30 minutes</li>
          </ul>
        </div>
*/}

        {/* Notifications 
        <div style={{ marginBottom: '32px', backgroundColor: '#fffae6', padding: '16px', borderRadius: '8px' }}>
          <h2 className="recent-missions-heading">Notifications</h2>
          <p>3 Missions are pending assignment. Check your mission queue.</p>
        </div>
*/}

        {/* Quick Access 
        <div style={{ display: 'flex', gap: '16px' }}>
          <button style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}>
            Create New Mission
          </button>
          <button style={{ padding: '8px 16px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px' }}>
            Manage Organizations
          </button>
        </div>
*/}


      </div>
    </Box>
  );
};

export default Dashboard;